import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/NewButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { updateFolder, FolderUpdate, Folder } from '@/hooks/useFolders';
import { apiErrorMessage } from '@/lib/api-client';
import { isClientDocumentsRoot } from '@/lib/eca-client-documents';

interface RenameFolderDialogProps {
  matterSlug: string;
  folder?: Folder;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const RenameFolderDialog: React.FC<RenameFolderDialogProps> = ({
  matterSlug,
  folder,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [folderName, setFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (folder) {
      setFolderName(folder.name);
    }
  }, [folder]);

  const handleRename = async () => {
    if (!folderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    if (!folder) return;

    if (folderName.trim() === folder.name) {
      onClose();
      return;
    }

    // Check if trying to rename a root folder to "Correspondence"
    if (!folder.parent_folder_id && folderName.trim().toLowerCase() === 'correspondence') {
      toast.error('Cannot rename a root folder to "Correspondence" - this is a system folder name');
      return;
    }
    if (isClientDocumentsRoot(folder)) {
      toast.error('Cannot rename the Client Documents folder');
      return;
    }

    setIsLoading(true);
    try {
      const folderData: FolderUpdate = {
        name: folderName.trim()
      };

      await updateFolder(matterSlug, folder.guid, folderData);
      toast.success('Folder renamed successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error renaming folder:', error);
      toast.error(apiErrorMessage(error) || 'Failed to rename folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFolderName(folder?.name || '');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Rename Folder
          </DialogTitle>
          <DialogDescription>
            Change the name of the folder "{folder?.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              placeholder="Enter folder name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                }
              }}
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading} text='Cancel' />
          <Button onClick={handleRename} disabled={isLoading || !folderName.trim()} loading={isLoading} text='Rename Folder' />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
